import { useState } from 'react'
import { api, errorMessage, useQuery } from '../api.js'
import { Alert, Button, Field, Input } from './ui.js'

export interface StepUpRequest {
  action: string
  title: string
  description: string
  /** hostname que o usuario precisa digitar; ausente = sem confirmacao extra */
  confirmHostname?: string
  /** exige justificativa em texto */
  requireReason?: boolean
  onProved: (proofId: string, reason?: string) => Promise<void>
}

/**
 * Modal de reconfirmacao.
 *
 * Tres barreiras deliberadas, na ordem em que reduzem erro:
 *  1. digitar o hostname — pega o clique na linha errada da lista;
 *  2. justificativa — obriga a formular a intencao antes de agir;
 *  3. codigo TOTP — prova que e a pessoa, e nao uma aba esquecida aberta.
 *
 * A terceira barreira e reaproveitada por 15 min: depois de digitar o codigo
 * uma vez, os comandos seguintes dentro da janela nao pedem de novo (a "sessao
 * elevada"). O hostname e o motivo continuam sendo pedidos — sao barreiras
 * contra erro, nao contra impostor, e valem a cada acao. Quem decide se a
 * elevacao ainda vale e o servidor; aqui so escondemos o campo do codigo.
 */
export function StepUpModal({ request, onClose }: { request: StepUpRequest; onClose: () => void }) {
  const elev = useQuery(() => api.auth.elevation.query(), [])
  const [code, setCode] = useState('')
  const [hostname, setHostname] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Se o quick falhar por elevacao recem-expirada, caimos para o codigo.
  const [forcarCodigo, setForcarCodigo] = useState(false)

  const ateQuando = elev.data?.elevatedUntil ? new Date(elev.data.elevatedUntil) : null
  const elevado = !forcarCodigo && ateQuando != null && ateQuando.getTime() > Date.now()
  const minutosRestantes = ateQuando ? Math.max(1, Math.round((ateQuando.getTime() - Date.now()) / 60000)) : 0

  const hostOk = !request.confirmHostname || hostname.trim().toLowerCase() === request.confirmHostname.toLowerCase()
  const reasonOk = !request.requireReason || reason.trim().length >= 5
  const codigoOk = elevado || code.replace(/\D/g, '').length === 6
  const ready = codigoOk && hostOk && reasonOk && !elev.loading

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const motivo = request.requireReason ? reason : undefined
      let proofId: string
      if (elevado) {
        try {
          const r = await api.auth.stepUpQuick.mutate({ action: request.action, reason: motivo })
          proofId = r.proofId
        } catch {
          // Elevacao expirou entre abrir o modal e confirmar: pede o codigo.
          setForcarCodigo(true)
          setError('Sua sessao elevada expirou. Digite o codigo do autenticador.')
          return
        }
      } else {
        const r = await api.auth.stepUp.mutate({ code, action: request.action, reason: motivo })
        proofId = r.proofId
      }
      await request.onProved(proofId, reason)
      onClose()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/45 p-4">
      <div className="w-full max-w-md rounded border border-line bg-panel p-6 shadow-xl">
        <h3 className="text-lg font-semibold tracking-tight">{request.title}</h3>
        <p className="mt-2 text-sm text-ink-2">{request.description}</p>

        <div className="mt-5 space-y-4">
          {request.confirmHostname && (
            <Field
              label="Confirme o servidor"
              hint={`Digite exatamente: ${request.confirmHostname}`}
            >
              <Input
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
                placeholder={request.confirmHostname}
                autoComplete="off"
              />
            </Field>
          )}

          {request.requireReason && (
            <Field label="Motivo" hint="Fica registrado na trilha de auditoria, junto do seu nome.">
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex.: manutencao programada da folha"
              />
            </Field>
          )}

          {elev.loading ? (
            <p className="text-sm text-ink-3">Verificando sua sessao...</p>
          ) : elevado ? (
            <div className="rounded border border-ok/40 bg-ok-soft px-3 py-2 text-sm text-ink-2">
              Sessao elevada — valida por mais {minutosRestantes} min. Nao e preciso digitar o
              codigo; a acao continua registrada na auditoria.
            </div>
          ) : (
            <Field label="Codigo do autenticador" hint="Seis digitos do app que voce configurou no primeiro acesso. Vale para os proximos 15 min.">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                className="font-mono text-lg tracking-[0.3em]"
                autoFocus
              />
            </Field>
          )}

          {error && <Alert>{error}</Alert>}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={submit} disabled={!ready || busy}>
            {busy ? 'Confirmando...' : 'Confirmar'}
          </Button>
        </div>
      </div>
    </div>
  )
}
